import React, { useState } from 'react';
import {
  MessageSquare, Mail, Bot, Filter, Users, X, Send,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { apiPost } from '../../lib/api.js';
import { BOT_DISPOSITION_OPTIONS } from './leadsConstants.jsx';

export function BulkSmsModal({ count, onClose, onSend }) {
  const [body, setBody] = useState('Hi {{lead.firstName}}, ');
  const [sending, setSending] = useState(false);
  const send = async () => {
    if (!body.trim()) return toast.error('Message body required');
    setSending(true);
    await onSend(body);
    setSending(false);
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60" onClick={onClose}>
      <div className="bg-civix-navy border border-white/10 rounded-xl w-full max-w-lg p-6 space-y-4" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold flex items-center gap-2"><MessageSquare className="w-5 h-5 text-civix-purple" /> Send bulk SMS</h2>
          <button onClick={onClose} className="p-1 text-white/60 hover:text-white"><X className="w-4 h-4" /></button>
        </div>
        <div className="text-sm text-white/70">
          To <span className="font-semibold text-white">{count}</span> selected lead(s). Blocked / opted-out leads are skipped automatically.
        </div>
        <textarea
          rows={6}
          className="input w-full"
          value={body}
          onChange={(e) => setBody(e.target.value)}
          placeholder="Hi {{lead.firstName}}, this is..."
        />
        <div className="text-xs text-white/40">
          Templates: <code>{'{{lead.firstName}}'}</code>, <code>{'{{lead.lastName}}'}</code>, <code>{'{{lead.state}}'}</code>
        </div>
        <div className="flex justify-end gap-2 pt-2 border-t border-white/10">
          <button onClick={onClose} className="btn-secondary text-sm">Cancel</button>
          <button onClick={send} disabled={sending} className="btn-primary text-sm flex items-center gap-2">
            <Send className="w-4 h-4" /> {sending ? 'Sending…' : `Send to ${count}`}
          </button>
        </div>
      </div>
    </div>
  );
}

export function BulkEmailModal({ count, onClose, onSend }) {
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('Hi {{lead.firstName}},\n\n');
  const [sending, setSending] = useState(false);
  const send = async () => {
    if (!subject.trim()) return toast.error('Subject required');
    if (!body.trim()) return toast.error('Body required');
    setSending(true);
    await onSend(subject, body);
    setSending(false);
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60" onClick={onClose}>
      <div className="bg-civix-navy border border-white/10 rounded-xl w-full max-w-lg p-6 space-y-4" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold flex items-center gap-2"><Mail className="w-5 h-5 text-civix-purple" /> Send bulk email</h2>
          <button onClick={onClose} className="p-1 text-white/60 hover:text-white"><X className="w-4 h-4" /></button>
        </div>
        <div className="text-sm text-white/70">
          To <span className="font-semibold text-white">{count}</span> selected lead(s) with email addresses on file.
        </div>
        <input className="input w-full" placeholder="Subject" value={subject} onChange={(e) => setSubject(e.target.value)} />
        <textarea
          rows={8}
          className="input w-full"
          value={body}
          onChange={(e) => setBody(e.target.value)}
          placeholder="Email body. {{lead.firstName}} works."
        />
        <div className="text-xs text-white/40">
          Templates: <code>{'{{lead.firstName}}'}</code>, <code>{'{{lead.email}}'}</code>
        </div>
        <div className="flex justify-end gap-2 pt-2 border-t border-white/10">
          <button onClick={onClose} className="btn-secondary text-sm">Cancel</button>
          <button onClick={send} disabled={sending} className="btn-primary text-sm flex items-center gap-2">
            <Send className="w-4 h-4" /> {sending ? 'Sending…' : `Send to ${count}`}
          </button>
        </div>
      </div>
    </div>
  );
}

export function DispositionToBotModal({ count, leadIds, onClose, onDone }) {
  const [label, setLabel] = useState('');
  const [phase, setPhase] = useState('pick');
  const [summary, setSummary] = useState(null);

  const confirmAndSend = async () => {
    if (!label) return toast.error('Pick an AI Schedule Bot first');
    setPhase('sending');
    try {
      const res = await apiPost('/leads/bulk/disposition-to-bot', { leadIds, dispositionLabel: label });
      setSummary(res.summary || {});
      setPhase('done');
    } catch (e) {
      toast.error(e.response?.data?.error || 'Disposition to bot failed');
      setPhase('pick');
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60" onClick={onClose}>
      <div className="bg-civix-navy border border-white/10 rounded-xl w-full max-w-lg p-6 space-y-4" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold flex items-center gap-2"><Bot className="w-5 h-5 text-civix-purple" /> Disposition to AI Bot</h2>
          <button onClick={onClose} className="p-1 text-white/60 hover:text-white"><X className="w-4 h-4" /></button>
        </div>

        {phase !== 'done' ? (
          <>
            <div className="rounded-lg bg-civix-purple/10 border border-civix-purple/30 px-3 py-2 text-sm">
              This will text <strong>{count.toLocaleString()}</strong> lead{count === 1 ? '' : 's'} now — the bot sends
              its first booking message immediately. Leads with no consent are sent under an audited owner
              override; DNC / opted-out leads are hard-blocked.
            </div>
            <label className="block text-sm text-white/70">
              AI Schedule Bot
              <select
                className="input w-full mt-1"
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                disabled={phase === 'sending'}
              >
                <option value="">Select a bot…</option>
                {BOT_DISPOSITION_OPTIONS.map((o) => (
                  <option key={o.label} value={o.label}>{o.product}</option>
                ))}
              </select>
            </label>
            <div className="flex justify-end gap-2 pt-2 border-t border-white/10">
              <button onClick={onClose} className="btn-secondary text-sm">Cancel</button>
              <button
                onClick={confirmAndSend}
                disabled={phase === 'sending' || !label}
                className="btn-primary text-sm flex items-center gap-2 disabled:opacity-50"
              >
                <Send className="w-4 h-4" /> {phase === 'sending' ? 'Sending…' : `Text ${count.toLocaleString()} leads now`}
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="text-sm text-white/80 space-y-1">
              <div className="flex justify-between"><span>Sent</span><strong className="text-green-300">{(summary?.sent || 0).toLocaleString()}</strong></div>
              <div className="flex justify-between"><span>Sent under override (no consent)</span><strong className="text-amber-300">{(summary?.overridden || 0).toLocaleString()}</strong></div>
              <div className="flex justify-between"><span>Blocked (DNC / opted-out)</span><strong className="text-red-300">{(summary?.blocked || 0).toLocaleString()}</strong></div>
              <div className="flex justify-between"><span>Failed / not found</span><strong className="text-white/60">{((summary?.errors || 0) + (summary?.notFound || 0) + (summary?.skipped || 0)).toLocaleString()}</strong></div>
            </div>
            <div className="flex justify-end gap-2 pt-2 border-t border-white/10">
              <button onClick={onDone} className="btn-primary text-sm">Done</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export function AddToPipelineModal({ count, leadIds, pipelines = [], onClose, onDone }) {
  const [pipelineId, setPipelineId] = useState('');
  const [stageId, setStageId] = useState('');
  const [phase, setPhase] = useState('pick');
  const [summary, setSummary] = useState(null);

  const pipeline = pipelines.find((p) => String(p._id) === String(pipelineId));
  const stages = (pipeline?.stages || []).filter((s) => !s.archived);

  const confirmAndAdd = async () => {
    if (!pipelineId) return toast.error('Pick a pipeline first');
    setPhase('saving');
    try {
      const body = { leadIds, pipelineId };
      if (stageId) body.stageId = stageId;
      const res = await apiPost('/leads/bulk/add-to-pipeline', body);
      setSummary(res.summary || {});
      setPhase('done');
    } catch (e) {
      toast.error(e.response?.data?.error || 'Add to pipeline failed');
      setPhase('pick');
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60" onClick={onClose}>
      <div className="bg-civix-navy border border-white/10 rounded-xl w-full max-w-lg p-6 space-y-4" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold flex items-center gap-2"><Filter className="w-5 h-5 text-civix-purple" /> Add to sales pipeline</h2>
          <button onClick={onClose} className="p-1 text-white/60 hover:text-white"><X className="w-4 h-4" /></button>
        </div>

        {phase !== 'done' ? (
          <>
            <div className="rounded-lg bg-civix-purple/10 border border-civix-purple/30 px-3 py-2 text-sm">
              This will add <strong>{count.toLocaleString()}</strong> lead{count === 1 ? '' : 's'} to
              {pipeline ? <> <strong>{pipeline.name}</strong></> : ' the selected pipeline'}. Leads already in
              this pipeline &amp; stage are skipped.
            </div>
            {pipelines.length === 0 ? (
              <div className="text-sm text-white/50">No pipelines yet — create one in Pipelines first.</div>
            ) : (
              <>
                <label className="block text-sm text-white/70">
                  Pipeline
                  <select
                    className="input w-full mt-1"
                    value={pipelineId}
                    onChange={(e) => { setPipelineId(e.target.value); setStageId(''); }}
                    disabled={phase === 'saving'}
                  >
                    <option value="">Select a pipeline…</option>
                    {pipelines.map((p) => (
                      <option key={p._id} value={p._id}>{p.name}</option>
                    ))}
                  </select>
                </label>
                {stages.length > 0 && (
                  <label className="block text-sm text-white/70">
                    Stage <span className="text-white/40">(defaults to the first stage)</span>
                    <select
                      className="input w-full mt-1"
                      value={stageId}
                      onChange={(e) => setStageId(e.target.value)}
                      disabled={phase === 'saving'}
                    >
                      <option value="">{stages[0]?.name} (default)</option>
                      {stages.map((s) => (
                        <option key={s._id} value={s._id}>{s.name}</option>
                      ))}
                    </select>
                  </label>
                )}
              </>
            )}
            <div className="flex justify-end gap-2 pt-2 border-t border-white/10">
              <button onClick={onClose} className="btn-secondary text-sm">Cancel</button>
              <button
                onClick={confirmAndAdd}
                disabled={phase === 'saving' || !pipelineId}
                className="btn-primary text-sm flex items-center gap-2 disabled:opacity-50"
              >
                {phase === 'saving' ? 'Adding…' : `Add ${count.toLocaleString()} lead${count === 1 ? '' : 's'}`}
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="text-sm text-white/80 space-y-1">
              <div className="flex justify-between"><span>Added</span><strong className="text-green-300">{(summary?.added || 0).toLocaleString()}</strong></div>
              <div className="flex justify-between"><span>Already in pipeline (skipped)</span><strong className="text-white/60">{(summary?.skipped || 0).toLocaleString()}</strong></div>
              <div className="flex justify-between"><span>Not found</span><strong className="text-white/60">{(summary?.notFound || 0).toLocaleString()}</strong></div>
              <div className="flex justify-between"><span>Failed</span><strong className="text-red-300">{(summary?.errors || 0).toLocaleString()}</strong></div>
            </div>
            <div className="flex justify-end gap-2 pt-2 border-t border-white/10">
              <button onClick={onDone} className="btn-primary text-sm">Done</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export function TransferToLeadAssistModal({ count, leadIds, onClose, onDone }) {
  const [phase, setPhase] = useState('confirm');
  const [summary, setSummary] = useState(null);

  const confirmAndTransfer = async () => {
    setPhase('working');
    try {
      const res = await apiPost('/leads/bulk/transfer-to-lead-assist', { leadIds });
      setSummary(res.summary || {});
      setPhase('done');
    } catch (e) {
      toast.error(e.response?.data?.error || 'Transfer to Lead Assist failed');
      setPhase('confirm');
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60" onClick={onClose}>
      <div className="bg-civix-navy border border-white/10 rounded-xl w-full max-w-lg p-6 space-y-4" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold flex items-center gap-2"><Users className="w-5 h-5 text-civix-purple" /> Transfer to Lead Assist</h2>
          <button onClick={onClose} className="p-1 text-white/60 hover:text-white"><X className="w-4 h-4" /></button>
        </div>

        {phase !== 'done' ? (
          <>
            <div className="rounded-lg bg-civix-purple/10 border border-civix-purple/30 px-3 py-2 text-sm">
              This will move <strong>{count.toLocaleString()}</strong> lead{count === 1 ? '' : 's'} into the shared
              Lead Assist pool. Lead Assist agents dial the pool directly. Each lead keeps its current owner — no
              texts or calls are sent.
            </div>
            <div className="flex justify-end gap-2 pt-2 border-t border-white/10">
              <button onClick={onClose} className="btn-secondary text-sm">Cancel</button>
              <button
                onClick={confirmAndTransfer}
                disabled={phase === 'working'}
                className="btn-primary text-sm flex items-center gap-2 disabled:opacity-50"
              >
                <Users className="w-4 h-4" /> {phase === 'working' ? 'Transferring…' : `Transfer ${count.toLocaleString()} leads to Lead Assist`}
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="text-sm text-white/80 space-y-1">
              <div className="flex justify-between"><span>Transferred to pool</span><strong className="text-green-300">{(summary?.transferred || 0).toLocaleString()}</strong></div>
              <div className="flex justify-between"><span>Already in pool (skipped)</span><strong className="text-amber-300">{(summary?.skipped || 0).toLocaleString()}</strong></div>
              <div className="flex justify-between"><span>Not found</span><strong className="text-white/60">{(summary?.notFound || 0).toLocaleString()}</strong></div>
              <div className="flex justify-between"><span>Failed</span><strong className="text-red-300">{(summary?.errors || 0).toLocaleString()}</strong></div>
            </div>
            <div className="flex justify-end gap-2 pt-2 border-t border-white/10">
              <button onClick={onDone} className="btn-primary text-sm">Done</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
